import { Activity, AlertTriangle, TrendingDown, UserMinus, Clock, CalendarX, ChevronDown, ChevronUp } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Progress } from "../../components/ui/progress";
import { Badge } from "../../components/ui/badge";
import { useState } from "react";

const signalMeta = {
  margin_compression: { label: "Margin Compression", icon: TrendingDown, color: "text-red-500" },
  overallocated_artists: { label: "Overallocated Artists", icon: Activity, color: "text-orange-500" },
  past_due_receivables: { label: "Past Due Receivables", icon: AlertTriangle, color: "text-amber-500" },
  programs_without_pm: { label: "Programs Without PM", icon: UserMinus, color: "text-blue-500" },
  execution_delays: { label: "Execution Delays", icon: Clock, color: "text-purple-500" },
  leave_conflicts: { label: "Leave Conflicts", icon: CalendarX, color: "text-pink-500" }
};

function SignalRow({ name, data, weight }) {
  const [open, setOpen] = useState(false);
  const meta = signalMeta[name];
  const Icon = meta?.icon || Activity;
  const hasIssues = data.count > 0;

  return (
    <div className="border-b border-border/50 last:border-0">
      <button
        onClick={() => hasIssues && setOpen(!open)}
        className="w-full flex items-center gap-3 py-3 hover:bg-muted/30 transition-colors px-1"
        disabled={!hasIssues}
      >
        <Icon className={`w-4 h-4 ${hasIssues ? meta?.color : "text-muted-foreground"}`} />
        <div className="flex-1 text-left">
          <p className="text-sm font-medium">{meta?.label || name}</p>
          <p className="text-xs text-muted-foreground">{Math.round(weight * 100)}% weight</p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-sm font-mono ${data.score < 70 ? "text-red-500" : data.score < 85 ? "text-amber-500" : "text-green-500"}`}>
            {data.score}
          </span>
          {hasIssues && (
            <Badge variant="secondary" className="text-xs">{data.count}</Badge>
          )}
          {hasIssues && (open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />)}
        </div>
      </button>
      {open && hasIssues && (
        <div className="px-4 pb-3 space-y-2">
          {data.issues?.slice(0, 3).map((issue, idx) => (
            <div key={idx} className="text-xs bg-muted/50 rounded p-2">
              {issue.program_name && <span className="font-medium">{issue.program_name}</span>}
              {issue.artist_name && <span className="font-medium">{issue.artist_name}</span>}
              {issue.resource_name && <span className="font-medium">{issue.resource_name}</span>}
              {issue.cost_pct && <span className="ml-2 text-muted-foreground">({issue.cost_pct}% cost)</span>}
              {issue.load_pct && <span className="ml-2 text-muted-foreground">({issue.load_pct}% load)</span>}
              {issue.outstanding && <span className="ml-2 text-muted-foreground">${issue.outstanding.toLocaleString()}</span>}
              {issue.days_overdue && <span className="ml-2 text-red-500">{issue.days_overdue}d overdue</span>}
            </div>
          ))}
          {data.count > 3 && <p className="text-xs text-muted-foreground">+{data.count - 3} more</p>}
        </div>
      )}
    </div>
  );
}

export default function HealthScore({ data, loading }) {
  if (loading) {
    return (
      <Card className="animate-pulse">
        <CardHeader><CardTitle className="h-6 bg-muted rounded w-48" /></CardHeader>
        <CardContent className="h-64" />
      </Card>
    );
  }

  if (!data) return null;

  const { health_score, status, status_label, signals, weights } = data;

  const statusColors = {
    healthy: "bg-green-500",
    caution: "bg-amber-500",
    warning: "bg-orange-500",
    critical: "bg-red-500"
  };

  return (
    <Card data-testid="health-score-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg" style={{ fontFamily: 'var(--font-sans)' }}>Operational Health</CardTitle>
          <Badge className={`${statusColors[status]} text-white`}>{status_label}</Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-6 mb-6">
          <div className="relative w-32 h-32">
            <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="45" fill="none" stroke="currentColor" strokeWidth="8" className="text-muted/30" />
              <circle
                cx="50" cy="50" r="45" fill="none" stroke="currentColor" strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray={`${health_score * 2.83} 283`}
                className={statusColors[status].replace("bg-", "text-")}
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center flex-col">
              <span className="text-3xl font-light" style={{ fontFamily: 'var(--font-sans)' }}>{health_score}</span>
              <span className="text-xs text-muted-foreground">/ 100</span>
            </div>
          </div>
          <div className="flex-1">
            <p className="text-sm text-muted-foreground mb-3">Signal Breakdown</p>
            <div className="space-y-2">
              {Object.entries(signals).slice(0, 3).map(([name, sig]) => (
                <div key={name} className="flex items-center gap-2">
                  <span className="text-xs w-24 truncate">{signalMeta[name]?.label}</span>
                  <Progress value={sig.score} className="h-1.5 flex-1" />
                  <span className="text-xs font-mono w-8 text-right">{sig.score}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="border-t pt-4">
          <p className="text-xs text-muted-foreground mb-2 uppercase tracking-wider">Risk Signals</p>
          {Object.entries(signals).map(([name, sig]) => (
            <SignalRow key={name} name={name} data={sig} weight={weights[name] || 0} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
